/**
 * Set Details Page - Browse all cards in a specific MTG set
 *
 * Displays cards with table/grid/summary views, filters, and ownership highlighting.
 * Uses MinimalCard data with on-demand full data loading for details panel.
 */
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { type ReactElement, useCallback, useMemo } from 'react';
import { IoArrowBack, IoCheckmarkCircle } from 'react-icons/io5';
import { Link, useParams } from 'react-router-dom';

import { useAtom, useAtomValue } from 'jotai';

import EmptyState from '@/components/EmptyState/EmptyState';
import Search from '@/components/Search/Search';
import SetIcon from '@/components/SetIcon/SetIcon';
import SortBySelector, { type SortOption } from '@/components/SortBySelector/SortBySelector';
import Spinner from '@/components/Spinner/Spinner';
import ViewControlsBar from '@/components/ViewControlsBar/ViewControlsBar';
import { collectionOwnedByScryfallIdAtom } from '@/features/collection/state/collectionAtom';
import { cardOracleIdsByDeckAtom, cardOracleIdsInDecksAtom } from '@/features/decks/state/allDeckCards';
import { listedOracleIdsAtom, oracleIdsByListAtom } from '@/features/lists/state/listsAtom';
import { matchesSearchQuery } from '@/lib/cardSearch';
import { SET_TYPE_LABELS } from '@/lib/cardUtils';
import type { MinimalCard } from '@/services/cardDatabase';

import SetCardDetailsPanel from './components/SetCardDetailsPanel';
import SetCardsGrid from './components/SetCardsGrid';
import SetCardsSummary from './components/SetCardsSummary';
import SetCardsTable from './components/SetCardsTable';
import SetFiltersPanel from './components/SetFiltersPanel';
import SetViewToggle from './components/SetViewToggle';
import { useSetByCode } from './hooks/api';
import { useSetCards } from './hooks/useSetCards';
import useSetFilters, { type SetFilters } from './hooks/useSetFilters';
import useSetSort, { createSetSortComparator, SET_SORT_FIELD_LABELS, type SetSortField } from './hooks/useSetSort';
import { selectedSetCardAtom, setViewModeAtom } from './state/setDetailsAtom';

// Sort options for grid/summary views
const SORT_OPTIONS: SortOption<SetSortField>[] = Object.entries(SET_SORT_FIELD_LABELS).map(([value, label]) => ({
  value: value as SetSortField,
  label,
}));

/**
 * Apply filters to a list of cards
 */
function applyFilters(
  cards: MinimalCard[],
  filters: SetFilters,
  ownedByScryfallId: Map<string, number>,
  oracleIdsInDecks: Set<string>,
  oracleIdsByDeck: Map<string, Set<string>>,
  listedOracleIds: Set<string>,
  oracleIdsByList: Map<string, Set<string>>,
): MinimalCard[] {
  return cards.filter((card) => {
    // Search filter (name, type line, or collector number for numeric queries)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const isNumericSearch = /^\d+$/.test(filters.search);

      if (isNumericSearch) {
        // Numeric search: match collector number as integer (e.g. "1" matches "0001")
        const searchNum = parseInt(filters.search, 10);
        const collectorNum = parseInt(card.collector_number, 10);
        if (collectorNum !== searchNum) return false;
      } else {
        if (!matchesSearchQuery({ nameLower: card.name_lower, typeLineLower: card.type_line?.toLowerCase() ?? '' }, searchLower))
          return false;
      }
    }

    // Rarity filter
    if (filters.rarities.length > 0) {
      if (!card.rarity || !filters.rarities.includes(card.rarity)) return false;
    }

    // Color filter (any match)
    if (filters.colors.length > 0) {
      const cardColors = card.color_identity ?? [];
      const hasMatchingColor = filters.colors.some((color) => cardColors.includes(color));
      if (!hasMatchingColor) return false;
    }

    // Special color filter (multicolor/colorless)
    if (filters.specialColors.length > 0) {
      const cardColors = card.color_identity ?? [];
      const hasMulticolorMatch = filters.specialColors.includes('multicolor') && cardColors.length >= 2;
      const hasColorlessMatch = filters.specialColors.includes('colorless') && cardColors.length === 0;
      if (!hasMulticolorMatch && !hasColorlessMatch) return false;
    }

    // Card type filter
    if (filters.cardTypes.length > 0) {
      const typeLine = card.type_line?.toLowerCase() ?? '';
      const hasMatchingType = filters.cardTypes.some((type) => typeLine.includes(type));
      if (!hasMatchingType) return false;
    }

    // CMC filter
    if (filters.cmcValues.length > 0) {
      const cmc = card.cmc ?? 0;
      const matchesCmc = filters.cmcValues.some((cmcFilter) => {
        if (cmcFilter === '6+') return cmc >= 6;
        return cmc === cmcFilter;
      });
      if (!matchesCmc) return false;
    }

    // Ownership filter
    if (filters.ownership !== 'all') {
      const isOwned = (ownedByScryfallId.get(card.id) ?? 0) > 0;
      if (filters.ownership === 'owned' && !isOwned) return false;
      if (filters.ownership === 'not-owned' && isOwned) return false;
    }

    // Deck membership filter
    if (filters.inDeckFilter) {
      const oracleId = card.oracle_id;
      if (!oracleId) return false;

      if (filters.deckIdFilter) {
        // Filter by specific deck
        const deckOracleIds = oracleIdsByDeck.get(filters.deckIdFilter);
        const isInDeck = deckOracleIds?.has(oracleId) ?? false;
        if (filters.inDeckFilter === 'in-deck' && !isInDeck) return false;
        if (filters.inDeckFilter === 'not-in-deck' && isInDeck) return false;
      } else {
        // Filter by any deck
        const isInAnyDeck = oracleIdsInDecks.has(oracleId);
        if (filters.inDeckFilter === 'in-deck' && !isInAnyDeck) return false;
        if (filters.inDeckFilter === 'not-in-deck' && isInAnyDeck) return false;
      }
    }

    // List membership filter
    if (filters.inListFilter) {
      const oracleId = card.oracle_id;
      if (!oracleId) return false;

      if (filters.listIdFilter) {
        // Filter by specific list
        const listOracleIds = oracleIdsByList.get(filters.listIdFilter);
        const isInList = listOracleIds?.has(oracleId) ?? false;
        if (filters.inListFilter === 'in-list' && !isInList) return false;
        if (filters.inListFilter === 'not-in-list' && isInList) return false;
      } else {
        // Filter by any list
        const isInAnyList = listedOracleIds.has(oracleId);
        if (filters.inListFilter === 'in-list' && !isInAnyList) return false;
        if (filters.inListFilter === 'not-in-list' && isInAnyList) return false;
      }
    }

    return true;
  });
}

export default function SetDetailsPage(): ReactElement {
  const { setCode } = useParams<{ setCode: string }>();
  const set = useSetByCode(setCode ?? null);
  const { cards, totalCount, isLoading: isLoadingCards } = useSetCards(setCode);
  const ownedByScryfallId = useAtomValue(collectionOwnedByScryfallIdAtom);
  const oracleIdsInDecks = useAtomValue(cardOracleIdsInDecksAtom);
  const oracleIdsByDeck = useAtomValue(cardOracleIdsByDeckAtom);
  const listedOracleIds = useAtomValue(listedOracleIdsAtom);
  const oracleIdsByList = useAtomValue(oracleIdsByListAtom);
  const [selectedCardId, setSelectedCardId] = useAtom(selectedSetCardAtom);
  const viewMode = useAtomValue(setViewModeAtom);
  const { sort, setSort } = useSetSort();
  const { filters, setSearch, hasActiveFilters } = useSetFilters();

  // Handle card selection
  const handleSelectCard = useCallback(
    (scryfallId: string) => {
      setSelectedCardId(scryfallId);
    },
    [setSelectedCardId],
  );

  // Handle closing the details panel
  const handleClosePanel = useCallback(() => {
    setSelectedCardId(null);
  }, [setSelectedCardId]);

  // Handle sort change
  const handleSortChange = useCallback(
    (field: SetSortField, direction: 'asc' | 'desc') => {
      setSort({ field, direction });
    },
    [setSort],
  );

  // Apply filters to cards
  const filteredCards = useMemo(() => {
    return applyFilters(cards, filters, ownedByScryfallId, oracleIdsInDecks, oracleIdsByDeck, listedOracleIds, oracleIdsByList);
  }, [cards, filters, ownedByScryfallId, oracleIdsInDecks, oracleIdsByDeck, listedOracleIds, oracleIdsByList]);

  // Apply sorting to filtered cards (only for grid/summary views - table uses TanStack sorting)
  const sortedCards = useMemo(() => {
    if (viewMode === 'table') {
      // Table view uses its own TanStack Table sorting
      return filteredCards;
    }
    const comparator = createSetSortComparator(sort, ownedByScryfallId);
    return [...filteredCards].sort(comparator);
  }, [filteredCards, sort, ownedByScryfallId, viewMode]);

  // Calculate ownership stats
  const ownershipStats = useMemo(() => {
    if (cards.length === 0) {
      return { ownedCount: 0, ownedUniqueCount: 0, percentage: 0 };
    }

    let ownedUniqueCount = 0;
    let totalOwnedQuantity = 0;

    for (const card of cards) {
      const quantity = ownedByScryfallId.get(card.id) ?? 0;
      if (quantity > 0) {
        ownedUniqueCount++;
        totalOwnedQuantity += quantity;
      }
    }

    const percentage = cards.length > 0 ? (ownedUniqueCount / cards.length) * 100 : 0;

    return {
      ownedCount: totalOwnedQuantity,
      ownedUniqueCount,
      percentage,
    };
  }, [cards, ownedByScryfallId]);

  // Format release date
  const releaseDate = useMemo(() => {
    if (!set?.released_at) return null;
    try {
      return format(parseISO(set.released_at), 'MMMM yyyy');
    } catch {
      return null;
    }
  }, [set?.released_at]);

  // Loading state
  if (isLoadingCards || !set) {
    return (
      <div className="flex h-full flex-col">
        {/* Back link */}
        <div className="border-b border-zinc-800 px-4 py-3 sm:px-6">
          <Link
            to="/sets"
            className="text-aether hover:text-aether/80 focus-visible:ring-aether inline-flex items-center gap-1.5 rounded text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            <IoArrowBack className="h-4 w-4" />
            Back to Sets
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // Set not found
  if (!set && !isLoadingCards) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-zinc-800 px-4 py-3 sm:px-6">
          <Link
            to="/sets"
            className="text-aether hover:text-aether/80 focus-visible:ring-aether inline-flex items-center gap-1.5 rounded text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            <IoArrowBack className="h-4 w-4" />
            Back to Sets
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <EmptyState
            title="Set not found"
            description={`The set "${setCode?.toUpperCase()}" could not be found.`}
            action={
              <Link
                to="/sets"
                className="text-aether hover:text-aether/80 focus-visible:ring-aether rounded text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
              >
                Browse all sets
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const setTypeLabel = SET_TYPE_LABELS[set.set_type] || set.set_type;

  return (
    <div className="flex h-full flex-col">
      {/* Back link */}
      <div className="border-b border-zinc-800 px-4 py-3 sm:px-6">
        <Link
          to="/sets"
          className="text-aether hover:text-aether/80 focus-visible:ring-aether inline-flex items-center gap-1.5 rounded text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <IoArrowBack className="h-4 w-4" />
          Back to Sets
        </Link>
      </div>

      {/* Header */}
      <header className="border-b border-zinc-800 px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          {/* Set info */}
          <div className="flex items-center gap-3 sm:gap-4">
            <SetIcon setCode={set.code} scryfallIconUri={set.icon_svg_uri} size="lg" className="text-3xl sm:text-4xl" />
            <div className="min-w-0">
              <h1 className="text-pearl truncate text-lg font-semibold sm:text-xl">
                {set.name} <span className="text-silver font-mono text-xs uppercase sm:text-sm">({set.code})</span>
              </h1>
              <p className="text-silver mt-0.5 text-xs sm:text-sm">
                {setTypeLabel}
                {releaseDate && <span> · Released {releaseDate}</span>}
              </p>
            </div>
          </div>

          {/* Collection stats */}
          <div className="flex items-center gap-3 sm:block sm:text-right">
            <div className="text-pearl text-base font-semibold sm:text-lg">{totalCount.toLocaleString()} cards</div>
            <div className="text-silver flex items-center gap-1.5 text-xs sm:mt-0.5 sm:justify-end sm:text-sm">
              <IoCheckmarkCircle
                className={clsx('h-4 w-4 shrink-0', ownershipStats.ownedUniqueCount > 0 ? 'text-emerald-400' : 'text-zinc-600')}
              />
              <span>
                <span className="sm:hidden">
                  {ownershipStats.ownedUniqueCount} owned ({ownershipStats.percentage.toFixed(0)}%)
                </span>
                <span className="hidden sm:inline">
                  You own {ownershipStats.ownedUniqueCount.toLocaleString()} cards ({ownershipStats.percentage.toFixed(1)}% complete)
                </span>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Controls bar + Filters */}
      <ViewControlsBar
        left={
          <div className="w-full min-w-0 sm:w-64">
            <Search onSearch={setSearch} placeholder="Search by name or card number…" />
          </div>
        }
        right={
          <>
            {/* Sort selector for grid/summary views (table uses column header sorting) */}
            {viewMode !== 'table' && (
              <SortBySelector
                value={sort.field}
                direction={sort.direction}
                options={SORT_OPTIONS}
                onChange={handleSortChange}
                variant="dropdown"
              />
            )}
            <SetViewToggle />
          </>
        }
        below={<SetFiltersPanel />}
      />

      {/* Card list (Table/Grid/Summary) */}
      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {filteredCards.length === 0 ? (
          hasActiveFilters ? (
            <EmptyState title="No cards match filters" description="Try adjusting your filters to see more cards." />
          ) : (
            <EmptyState title="No cards found" description="This set has no cards in the database." />
          )
        ) : viewMode === 'table' ? (
          <SetCardsTable
            cards={filteredCards}
            ownedQuantities={ownedByScryfallId}
            onSelectCard={handleSelectCard}
            selectedCardId={selectedCardId}
          />
        ) : viewMode === 'grid' ? (
          <SetCardsGrid
            cards={sortedCards}
            ownedQuantities={ownedByScryfallId}
            onSelectCard={handleSelectCard}
            selectedCardId={selectedCardId}
          />
        ) : (
          <SetCardsSummary
            cards={sortedCards}
            ownedQuantities={ownedByScryfallId}
            onSelectCard={handleSelectCard}
            selectedCardId={selectedCardId}
            sortField={sort.field}
          />
        )}
      </div>

      {/* Card Details Panel */}
      <SetCardDetailsPanel selectedCardId={selectedCardId} setName={set.name} setCode={set.code} onClose={handleClosePanel} />
    </div>
  );
}
